import { useEffect, useReducer, useRef, useState } from 'react';
import Observable from 'zen-observable';
import { calendarInviteCompleteSubscription } from '../config/appsync';
import { OnCalendarInviteCompletedSubscription } from '../config/appsync/API';
import { Hub } from 'aws-amplify';
import { AttendeeUrlProcessingPolling } from '../lib/services/polling/attendee-url-processing-polling';
import { AttendeeProcessingResultType } from '../lib/api';
import { CALENDAR_ADD_ERROR_MESSAGE } from '../lib/constants';

type SubscriptionData = { value: { data: OnCalendarInviteCompletedSubscription }};

export type SubscriptionError = { reason: string, message: string };

export type CalendarInviteSubscriptionState = {
  inProgress: boolean,
  subscribed: null | boolean,
  error: null | SubscriptionError
}

export enum ACTION_TYPE {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED'
}

type ACTION =
  | { type: ACTION_TYPE.INITIALIZED }
  | { type: ACTION_TYPE.COMPLETED; payload: boolean }
  | { type: ACTION_TYPE.FAILED, payload?: string }
  | { type: ACTION_TYPE.CANCELLED }
;

type CalendarResponse = { registrationId: string, appSyncToken: string };

export type CancelSubscription = () => void;

export type SubscriptionResult = {
  state: CalendarInviteSubscriptionState,
  subscribe(response: CalendarResponse | void): Promise<void | CancelSubscription>
}

export const SUBSCRIPTION_TIMEOUT_ERROR = 'SUBSCRIPTION_TIMEOUT';

export const getErrorByKey = (key?: string): SubscriptionError => {
  switch (key) {
  case SUBSCRIPTION_TIMEOUT_ERROR:
    return {
      reason: key,
      message: CALENDAR_ADD_ERROR_MESSAGE
    };
  case 'NOT_AN_ATTENDEE':
    return {
      reason: key,
      message: `Your email address is not currently on the list of registered attendees for this event.`
    };
  default:
    return {
      reason: key || '',
      message: key ? key : `Something went wrong. Please try again`
    };
  }
};

const _defaults: CalendarInviteSubscriptionState = { inProgress: false, error: null, subscribed: null };

export function reducer(state: CalendarInviteSubscriptionState, action: ACTION): CalendarInviteSubscriptionState {
  switch (action.type) {
  case ACTION_TYPE.INITIALIZED:
    return { ..._defaults, inProgress: true };
  case ACTION_TYPE.COMPLETED:
    return { ..._defaults, subscribed: action.payload };
  case ACTION_TYPE.FAILED:
    return { ..._defaults, error: getErrorByKey(action.payload) };
  case ACTION_TYPE.CANCELLED:
    return { ..._defaults, subscribed: false };
  default:
    return _defaults;
  }
}


const SUBSCRIPTION_TIMEOUT_MS = 30*1000;
const initialState = { inProgress: false, subscribed: null, error: null };

type SubscriptionHandles = {
  timeout: number | null,
  client: { unsubscribe(): void, closed: boolean } | null,
  stopListening: (() => void)[]
};

export const useCalendarInviteSubscription = (): SubscriptionResult => {
  const [ state, dispatch ] = useReducer(reducer, initialState);
  const cancelledRef = useRef<boolean>(false);
  const cancelSubscription = () => { cancelledRef.current = true; };
  const [ handles ] = useState<SubscriptionHandles>(() => ({ timeout: null, client: null, stopListening: []}));

  const clearSubscriptionTimeout = () => {
    if (handles.timeout) {
      window.clearTimeout(handles.timeout);
      handles.timeout = null;
    }
  };

  useEffect(() => () => {
    if (handles.timeout) window.clearTimeout(handles.timeout);
    if (handles.client && !handles.client.closed) handles.client.unsubscribe();
    handles.stopListening.forEach((stop) => stop());
  }, [handles]);

  const initSubscription = (client: {unsubscribe(): void, closed: boolean}) => {
    handles.client = client;
    clearSubscriptionTimeout();
    handles.timeout = window.setTimeout(() => {
      if (!client.closed) client.unsubscribe();
      subscriptionFailed(SUBSCRIPTION_TIMEOUT_ERROR);
      cancelSubscription();
    }, SUBSCRIPTION_TIMEOUT_MS);

    dispatch({ type: ACTION_TYPE.INITIALIZED });
  };
  const subscriptionCompleted = (payload: boolean) => dispatch({ type: ACTION_TYPE.COMPLETED, payload });
  const subscriptionFailed = (payload?: string) => dispatch({ type: ACTION_TYPE.FAILED, payload });
  const subscriptionCancelled = () => dispatch({ type: ACTION_TYPE.CANCELLED });

  return {
    state,
    async subscribe(response: CalendarResponse): Promise<void | CancelSubscription> {
      return new Promise((resolve, reject) => {
        const params = [response?.registrationId, response?.appSyncToken] as [string, string];

        if (!params.every(Boolean)) return resolve();

        cancelledRef.current = false;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore*/
        const client = (calendarInviteCompleteSubscription(...params) as Observable<SubscriptionData>)
          .subscribe({
            next({ value }: SubscriptionData): void {
              const subscriptionStatus = value?.data?.onCalendarInviteCompleted?.subscribed;
              const error = value?.data?.onCalendarInviteCompleted?.error;
              const cancelled = cancelledRef.current;

              if (!error && typeof subscriptionStatus !== 'boolean') {
                clearSubscriptionTimeout();
                subscriptionFailed();
                client.unsubscribe();
                return;
              }

              if (!cancelled && !error) {
                clearSubscriptionTimeout();
                subscriptionCompleted(subscriptionStatus);
              }

              if (!cancelled && error) {
                subscriptionFailed(error);
                clearSubscriptionTimeout();
              }

              if (cancelled && !error) {
                clearSubscriptionTimeout();
                subscriptionStatus ? subscriptionCompleted(subscriptionStatus) : subscriptionCancelled();
              }

              client.unsubscribe();
            },
            error(): void {
              clearSubscriptionTimeout();
              subscriptionFailed();
              client.unsubscribe();
            },
            complete(): void {
              clearSubscriptionTimeout();
              if (!client.closed) client.unsubscribe();
            }
          })
        ;

        initSubscription(client);

        const manuallyCancelSubscription: CancelSubscription = () => {
          if (!client.closed) client.unsubscribe();
          clearSubscriptionTimeout();
          subscriptionCancelled();
        };

        const cancelSubscriptionOnConnectionFailure = (): void => {
          if (!client.closed) client.unsubscribe();
          clearSubscriptionTimeout();
          subscriptionFailed();
          cancelSubscription();
        };

        /* Resolve when the subscription connection is established or reject if client fails to connect */
        const stopListening = Hub.listen('api', ({ payload }) => {
          const { event, data } = payload;

          if (event === 'Subscription ack') resolve(manuallyCancelSubscription);

          if (data.connectionState === 'ConnectionDisrupted') {

            /* stop subscription client */
            if (!client.closed) client.unsubscribe();
            clearSubscriptionTimeout();

            const polling = new AttendeeUrlProcessingPolling(response?.registrationId, response?.appSyncToken);

            /* start api polling */
            polling.start()
              .then((response) => {
                if (response.type === AttendeeProcessingResultType.CALENDAR_INVITE_DETAILS) {
                  subscriptionCompleted(response.subscribed || false);
                } else {
                  subscriptionFailed();
                }
              })
              .catch(() => {
                cancelSubscriptionOnConnectionFailure();
                reject(new Error('ConnectionDisrupted'));
              });

            /* resolving manual cancellation fn when polling subscription connection established */
            polling.subscribeToPollingStart(() => {
              resolve(() => {
                polling.stop();
                subscriptionCancelled();
              });
            });
          }
        });

        if (typeof stopListening === 'function') handles.stopListening.push(stopListening);
      });
    }
  };
};